import moment from "moment-timezone";

// A 12 hour datetime format, e.g. 'Nov 12th, 2019 11:28 am'.
const DEFAULT_TIME_FORMAT = "MMM Do, YYYY hh:mm a";
const DEFAULT_TIME_FORMAT_WITH_TZ = "MMM Do, YYYY hh:mm a z";
const TIME_FORMAT_WITH_DAY = "ddd, MMM Do, YYYY hh:mm a";

function calculateTimeDiff(timeDiff) {
  if (!timeDiff) {
    return "N/A";
  }

  const hours = timeDiff.asHours().toFixed(1);
  const absHours = Math.abs(hours);
  if (absHours === 0) {
    return "Your Time";
  }

  const suffix = hours >= 0 ? "ahead" : "behind";

  return `${absHours}h ${suffix}`;
}

function dateStringToDateFormat(dateStr, format = DEFAULT_TIME_FORMAT) {
  return moment(dateStr).format(format);
}

function formatDate(dateStr, format = DEFAULT_TIME_FORMAT, empty = "N/A") {
  // This formatter takes timestamp which has no timezone and is relative to
  // device's local clock.
  return dateStr ? dateStringToDateFormat(dateStr, format) : empty;
}

/**
 * Calculates a new time based on a calculated duration from current time.
 * @param {Duration} difference Moment duration used to calculate time
 * @param {string} format Date output format
 * @param {string} empty Value to return when input is invalid
 * @return {*} Value provided in `empty` param, or the formatted time
 */
function formatDateFromDifference(
  difference,
  format = DEFAULT_TIME_FORMAT,
  empty = "N/A",
) {
  if (!difference || !difference.isValid()) {
    return empty;
  }

  return moment().add(difference).format(format);
}

function formatDateWithAllTimezones(
  dateStr,
  format = DEFAULT_TIME_FORMAT,
  empty = "N/A",
) {
  // This formatter takes timestamp which has no timezone and is relative to
  // device's local clock.
  return dateStr
    ? `${dateStringToDateFormat(dateStr, format)} (All Timezones)`
    : empty;
}

/**
 * Parses a provided time in UTC to a specified timezone.
 * @param {string | Object} dateStr Date string or moment-like object to be
 * parsed
 * @param {string} timezone Timezone string or UTC offset
 * @param {string} empty Value to return when input is invalid
 * @return {*} Value provided in `empty` param, or the parsed object
 */
function parseDateWithTimezoneOrOffset(dateStr, timezone, empty = "N/A") {
  if (!dateStr || !timezone) {
    return empty;
  }

  if (moment.tz.zone(timezone)) {
    return moment.utc(dateStr).tz(timezone);
  }

  return moment.utc(dateStr).utcOffset(timezone);
}

function timeSince(utcTime, suffix = false, def = "N/A") {
  if (utcTime) {
    const utc = moment.utc(utcTime);
    const diff = utc.diff(moment.utc());
    return moment.duration(diff).humanize(suffix);
  }

  return def;
}

/**
 * Calculates the time difference between a specified timezone and local.
 * @param {string | Object} time Date string or moment-like object to be parsed
 * @param {string} timezone Timezone string or UTC offset
 * @param {string} empty Value to return when input is invalid
 * @return {*} Value provided in `empty` param, or difference between parsed
 * time and local time
 */
function timeDifferenceWithTimezone(time, timezone, empty = null) {
  if (!time || !timezone) {
    return empty;
  }

  const parsedDate = parseDateWithTimezoneOrOffset(time, timezone);
  const local = moment(parsedDate).local();
  const diff = parsedDate.local(true).diff(local);

  return moment.duration(diff);
}

function utcToLocal(utcTime, def = "N/A") {
  return utcToLocalFormat(utcTime, DEFAULT_TIME_FORMAT, def);
}

function utcToLocalFormat(utcTime, format = DEFAULT_TIME_FORMAT, def = "N/A") {
  if (utcTime) {
    // By utilizing moment.tz we can format timezone with formatting token 'z',
    // otherwise it will show nothing.
    const utc = moment.tz(utcTime, "UTC");
    const local = utc.tz(moment.tz.guess());
    return local.format(format);
  }

  return def;
}

function durationToHuman(msDuration) {
  if (typeof msDuration !== "number") {
    return "N/A";
  }

  const duration = moment.duration(msDuration);

  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  const h = hours ? hours + " h" : null;
  const m = minutes ? minutes + " m" : null;
  const s = seconds ? seconds + " s" : null;

  return [h, m, s].filter((t) => t).join(" ");
}

export {
  calculateTimeDiff,
  dateStringToDateFormat,
  DEFAULT_TIME_FORMAT,
  DEFAULT_TIME_FORMAT_WITH_TZ,
  durationToHuman,
  formatDate,
  formatDateFromDifference,
  formatDateWithAllTimezones,
  parseDateWithTimezoneOrOffset,
  TIME_FORMAT_WITH_DAY,
  timeSince,
  timeDifferenceWithTimezone,
  utcToLocal,
  utcToLocalFormat,
};
